import type { Scene } from '@babylonjs/core/scene';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { CreateLineSystem } from '@babylonjs/core/Meshes/Builders/linesBuilder';
import type { DebugConfig, DebugLayer } from '../config/debug.config';
import type { JunctionConfig } from '../config/level.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { ConnectorDefinition } from '../level/connectors/ConnectorDefinition';
import { transformPoint, type Pose } from '../level/connectors/Pose';
import type { SegmentDefinition } from '../level/segments/SegmentDefinition';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import { pathToLocal } from '../level/segments/segmentPath';
import type { TrackPresenter } from '../level/TrackPresenter';
import type { Vec3 } from '../utils/math';
import type { DebugManager } from './DebugManager';

type SocketLayer = Exclude<DebugLayer, 'colliders'>;
const SOCKET_LAYERS: readonly SocketLayer[] = [
  'laneGuides',
  'connectors',
  'obstacleSpawnPoints',
  'collectibleSpawnPoints',
  'decorationSockets',
  'dynamicEventSockets',
  'adSlots',
];

/** Debug overlays render after the world without depth test against it – visible through walls. */
const DEBUG_RENDERING_GROUP = 1;
const LIFT = 0.04;

interface LineSet {
  readonly lines: Vector3[][];
  readonly colors: Color4[][];
}

/**
 * Visualises segment data (connectors, lanes, sockets, ad slots) as line
 * gizmos. Essential for checking Blender exports against the segment data.
 * Built lazily – zero cost while debug mode is off.
 */
export class SocketGizmoPresenter {
  private readonly gizmos = new Map<number, Map<SocketLayer, LinesMesh>>();
  private readonly segments = new Map<number, SegmentInstance>();

  constructor(
    private readonly scene: Scene,
    private readonly track: TrackPresenter,
    private readonly debug: DebugManager,
    private readonly config: DebugConfig,
    private readonly junctions: JunctionConfig,
    events: EventBus<GameEvents>,
  ) {
    scene.setRenderingAutoClearDepthStencil(DEBUG_RENDERING_GROUP, true);
    events.on('segment:spawned', ({ segment }) => {
      this.segments.set(segment.uid, segment);
      this.sync(segment);
    });
    events.on('segment:recycled', ({ segment }) => {
      this.segments.delete(segment.uid);
      for (const mesh of this.gizmos.get(segment.uid)?.values() ?? []) mesh.dispose();
      this.gizmos.delete(segment.uid);
    });
    debug.onChange(() => {
      for (const segment of this.segments.values()) this.sync(segment);
    });
  }

  private sync(segment: SegmentInstance): void {
    let meshes = this.gizmos.get(segment.uid);
    for (const layer of SOCKET_LAYERS) {
      const visible = this.debug.isVisible(layer);
      let mesh = meshes?.get(layer);
      if (visible && !mesh) {
        mesh = this.build(segment, layer);
        if (!meshes) {
          meshes = new Map();
          this.gizmos.set(segment.uid, meshes);
        }
        meshes.set(layer, mesh);
      }
      mesh?.setEnabled(visible);
    }
  }

  private build(segment: SegmentInstance, layer: SocketLayer): LinesMesh {
    const set: LineSet = { lines: [], colors: [] };
    const color = Color3.FromHexString(this.config.colors[layer]).toColor4(1);
    const def = segment.definition;
    switch (layer) {
      case 'laneGuides':
        this.laneGuides(def, set, color);
        break;
      case 'connectors':
        this.connector(def.entry, set, Color3.FromHexString(this.config.colors.connectorIn).toColor4(1), def.width);
        for (const exit of def.exits) this.connector(exit, set, Color3.FromHexString(this.config.colors.connectorOut).toColor4(1), def.width);
        if (def.junction) this.junctionMarkers(def, set, color);
        break;
      case 'obstacleSpawnPoints':
        for (const p of def.obstacleSpawnPoints) this.square(set, color, p.position, 0.9);
        break;
      case 'collectibleSpawnPoints':
        for (const p of def.collectibleSpawnPoints) this.diamond(set, color, p.position, 0.18);
        break;
      case 'decorationSockets':
        for (const s of def.decorationSockets) {
          const pose: Pose = { ...s.position, yaw: s.yaw };
          this.rect(set, color, pose, s.maxFootprint.width, s.maxFootprint.depth);
          this.arrow(set, color, pose, 1.5);
        }
        break;
      case 'dynamicEventSockets':
        for (const s of def.dynamicEventSockets) {
          const base = s.position;
          set.lines.push([v(base.x, base.y, base.z), v(base.x, base.y + 3, base.z)]);
          set.colors.push([color, color]);
          this.diamond(set, color, { x: base.x, y: base.y + 3, z: base.z }, 0.5);
          this.arrow(set, color, { ...base, yaw: s.yaw }, 2);
        }
        break;
      case 'adSlots':
        for (const slot of def.advertisingSlots) {
          const pose: Pose = { ...slot.position, yaw: slot.yaw };
          const hw = slot.width / 2;
          const hh = slot.height / 2;
          const corners = [
            [-hw, -hh],
            [hw, -hh],
            [hw, hh],
            [-hw, hh],
            [-hw, -hh],
          ].map(([x, y]) => local(pose, { x: x ?? 0, y: y ?? 0, z: 0 }));
          set.lines.push(corners);
          set.colors.push(corners.map(() => color));
          this.arrow(set, color, pose, 1);
        }
        break;
    }

    // Socket data is in path coordinates: bend it onto the arc of curve modules (connectors are segment-local already).
    if (def.curve && layer !== 'connectors') {
      for (const line of set.lines) {
        for (const point of line) {
          const at = pathToLocal(def, point);
          point.set(at.x, at.y, at.z);
        }
      }
    }
    const mesh = CreateLineSystem(`DEBUG_${layer}_${segment.uid}`, { lines: set.lines, colors: set.colors }, this.scene);
    mesh.parent = this.track.rootOf(segment);
    mesh.isPickable = false;
    mesh.renderingGroupId = DEBUG_RENDERING_GROUP;
    return mesh;
  }

  private laneGuides(def: SegmentDefinition, set: LineSet, color: Color4): void {
    const dim = new Color4(color.r, color.g, color.b, 0.45);
    for (const lane of def.lanes) {
      for (const x of [lane.centerX - lane.width / 2, lane.centerX + lane.width / 2]) {
        // in 2 m pieces, so the guides bend with curve modules
        const line: Vector3[] = [];
        for (let z = 0; z < def.length + 1; z += 2) line.push(v(x, LIFT, Math.min(z, def.length)));
        set.lines.push(line);
        set.colors.push(line.map(() => dim));
      }
      // dashed center line
      for (let z = 0; z < def.length; z += 2) {
        set.lines.push([v(lane.centerX, LIFT, z), v(lane.centerX, LIFT, Math.min(z + 1, def.length))]);
        set.colors.push([color, color]);
      }
    }
  }

  private connector(connector: ConnectorDefinition, set: LineSet, color: Color4, width: number): void {
    const t = connector.transform;
    const across = [local(t, { x: -width / 2, y: LIFT, z: 0 }), local(t, { x: width / 2, y: LIFT, z: 0 })];
    set.lines.push(across);
    set.colors.push([color, color]);
    set.lines.push([local(t, { x: 0, y: 0, z: 0 }), local(t, { x: 0, y: 2.5, z: 0 })]);
    set.colors.push([color, color]);
    this.arrow(set, color, { ...t, y: t.y + 2.5 }, 1.5);
  }

  /** Pivot post and the start of the turn decision window. */
  private junctionMarkers(def: SegmentDefinition, set: LineSet, color: Color4): void {
    const pivot = def.junction?.pivot;
    if (!pivot) return;
    set.lines.push([v(pivot.x, 0, pivot.z), v(pivot.x, 4, pivot.z)]);
    set.lines.push([v(pivot.x - 0.5, LIFT, pivot.z), v(pivot.x + 0.5, LIFT, pivot.z)], [v(pivot.x, LIFT, pivot.z - 0.5), v(pivot.x, LIFT, pivot.z + 0.5)]);
    set.colors.push([color, color], [color, color], [color, color]);
    const windowZ = Math.max(0, pivot.z - this.junctions.decisionWindow);
    for (let x = -def.width / 2; x < def.width / 2; x += 0.6) {
      set.lines.push([v(x, LIFT, windowZ), v(Math.min(x + 0.3, def.width / 2), LIFT, windowZ)]);
      set.colors.push([color, color]);
    }
  }

  private arrow(set: LineSet, color: Color4, pose: Pose, length: number): void {
    const tip = { x: 0, y: LIFT, z: length };
    const head = length * 0.25;
    set.lines.push([local(pose, { x: 0, y: LIFT, z: 0 }), local(pose, tip)]);
    set.lines.push([local(pose, { x: -head, y: LIFT, z: length - head }), local(pose, tip), local(pose, { x: head, y: LIFT, z: length - head })]);
    set.colors.push([color, color], [color, color, color]);
  }

  private square(set: LineSet, color: Color4, p: Readonly<Vec3>, size: number): void {
    const h = size / 2;
    const y = p.y + LIFT;
    const pts = [v(p.x - h, y, p.z - h), v(p.x + h, y, p.z - h), v(p.x + h, y, p.z + h), v(p.x - h, y, p.z + h), v(p.x - h, y, p.z - h)];
    set.lines.push(pts, [v(p.x - h, y, p.z - h), v(p.x + h, y, p.z + h)], [v(p.x + h, y, p.z - h), v(p.x - h, y, p.z + h)]);
    set.colors.push(pts.map(() => color), [color, color], [color, color]);
  }

  private diamond(set: LineSet, color: Color4, p: Readonly<Vec3>, r: number): void {
    const pts = [v(p.x, p.y + r, p.z), v(p.x + r, p.y, p.z), v(p.x, p.y - r, p.z), v(p.x - r, p.y, p.z), v(p.x, p.y + r, p.z)];
    set.lines.push(pts);
    set.colors.push(pts.map(() => color));
  }

  private rect(set: LineSet, color: Color4, pose: Pose, width: number, depth: number): void {
    const hw = width / 2;
    const hd = depth / 2;
    const pts = [
      [-hw, -hd],
      [hw, -hd],
      [hw, hd],
      [-hw, hd],
      [-hw, -hd],
    ].map(([x, z]) => local(pose, { x: x ?? 0, y: LIFT, z: z ?? 0 }));
    set.lines.push(pts);
    set.colors.push(pts.map(() => color));
  }
}

function v(x: number, y: number, z: number): Vector3 {
  return new Vector3(x, y, z);
}

/** Point in a socket's local frame (x right, y up, z = socket direction) → segment-local Vector3. */
function local(pose: Pose, p: Readonly<Vec3>): Vector3 {
  const q = transformPoint(pose, p);
  return new Vector3(q.x, q.y, q.z);
}
